import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, API_BASE, type ApiResponse } from "@/api/client";
import { sortBySearchMatch } from "@ckgo2/shared";
import type { PremiumUpgrade } from "@ckgo2/shared/types";
import { Plus, Edit, Trash2, Loader2, Search } from "lucide-react";

export default function PremiumUpgradesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  const badgeLabels: Record<string, string> = {
    POPULAR: t("finance.premiumUpgrades.badges.popular"),
    "OUR TIP": t("finance.premiumUpgrades.badges.ourTip"),
    DISCOUNT: t("finance.premiumUpgrades.badges.discount"),
    NEW: t("finance.premiumUpgrades.badges.new"),
  };

  const { data, isLoading } = useQuery({
    queryKey: ["premiumUpgrades"],
    queryFn: () => api.get<ApiResponse<PremiumUpgrade[]>>("/api/admin/premium-upgrades"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/premium-upgrades/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["premiumUpgrades"] }),
  });

  const upgrades = sortBySearchMatch(data?.data || [], search, {
    name: (u) => u.title,
    other: () => [],
    tieBreak: () => 0,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("finance.premiumUpgrades.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("finance.premiumUpgrades.subtitle")}
          </p>
        </div>
        <Link to="/premium-upgrades/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("finance.premiumUpgrades.newUpgrade")}
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("finance.premiumUpgrades.searchPlaceholder")}
          className="input pl-10"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
        </div>
      ) : upgrades.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
          <p className="text-gray-500">{t("finance.premiumUpgrades.empty")}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("common.fields.image")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("common.fields.title")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("finance.premiumUpgrades.badge")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("common.fields.status")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("common.fields.sortOrder")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("common.fields.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
              {upgrades.map((upgrade) => (
                <tr key={upgrade.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3">
                    {upgrade.image ? (
                      <img
                        src={`${API_BASE}${upgrade.image}`}
                        alt={upgrade.title}
                        className="h-10 w-14 rounded object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-14 items-center justify-center rounded bg-gray-100 text-xs text-gray-400">
                        {t("finance.premiumUpgrades.noImage")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/premium-upgrades/${upgrade.id}`}
                      className="hover:text-primary-600 font-medium text-gray-900 dark:text-white"
                    >
                      {upgrade.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    {upgrade.badge && (
                      <span className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                        {badgeLabels[upgrade.badge] ?? upgrade.badge}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        upgrade.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {upgrade.isActive ? t("common.status.active") : t("common.status.inactive")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{upgrade.sortOrder}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/premium-upgrades/${upgrade.id}`}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button
                        title={t("common.actions.delete")}
                        type="button"
                        onClick={() => {
                          if (confirm(t("finance.premiumUpgrades.confirmDelete"))) {
                            deleteMutation.mutate(upgrade.id);
                          }
                        }}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
